import { put, call, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { Role } from '../../lib/roles';
import { fetchAgents } from '../apis/chat';
import { fetchTeamMembers } from '../apis/team';
import { errMessage } from '../../lib/apiError';
import { selectCoachRequests } from '../selectors/coach';
import { fetchCoachReassignments, decideCoachReassignment } from '../apis/coach';
import {
  toCoachOptions,
  isAlreadyDecided,
  presenceByUserId,
  coachDecisionHint,
} from '../../lib/coach';
import {
  setRequestPage,
  getCoachesAction,
  getCoachesSuccess,
  getCoachesFailure,
  setRequestStatusFilter,
  getCoachRequestsAction,
  getCoachRequestsSuccess,
  getCoachRequestsFailure,
  decideCoachRequestAction,
  decideCoachRequestSuccess,
  decideCoachRequestFailure,
} from '../slices/coach';

function* handleGetRequests() {
  try {
    const { status, page } = yield select(selectCoachRequests);
    const response = yield call(fetchCoachReassignments, {
      page,
      status: status === 'all' ? undefined : status,
    });
    // The schema documents a bare array here while the rest of the chat surface uses
    // the paginated envelope, so accept either rather than render an empty queue.
    const rows = Array.isArray(response) ? response : response?.data || response?.results || [];
    // ?status= is undeclared and may be ignored, so filter again client-side.
    const items = status && status !== 'all' ? rows.filter((r) => r.status === status) : rows;
    yield put(
      getCoachRequestsSuccess({
        items,
        total: Number.isFinite(response?.total_records) ? response.total_records : items.length,
      })
    );
  } catch (error) {
    yield put(getCoachRequestsFailure(errMessage(error, 'Failed to load coach requests')));
  }
}

function* handleDecide(action) {
  const { id, status, mode, coachId, remark } = action.payload;
  try {
    const updated = yield call(decideCoachReassignment, id, { status, mode, coachId, remark });
    yield put(decideCoachRequestSuccess(updated));
    const { page, total } = yield select(selectCoachRequests);
    const lastPage = Math.max(1, Math.ceil(total / 50));
    yield put(page > lastPage ? setRequestPage(lastPage) : getCoachRequestsAction());
  } catch (error) {
    // Failures leave the request pending, so the row stays decidable. A concurrent
    // decision is the exception: re-read instead.
    yield put(decideCoachRequestFailure(coachDecisionHint(error) || errMessage(error, 'Failed')));
    if (isAlreadyDecided(error)) yield put(getCoachRequestsAction());
  }
}

function* handleGetCoaches() {
  try {
    // coach_id is a TeamMember id. The agents roster returns User ids and excludes
    // anyone who has never signed in, so the team list is the only correct source.
    const response = yield call(fetchTeamMembers);
    const members = Array.isArray(response) ? response : response?.data || response?.results || [];

    // Presence is display-only, and absent for a coach who has never logged in.
    let presence = {};
    try {
      const roster = yield call(fetchAgents, { role: Role.SLEEP_COACH, limit: 200 });
      const rows = Array.isArray(roster) ? roster : roster?.data || roster?.results || [];
      presence = presenceByUserId(rows);
    } catch {
      // A missing presence column is not worth failing the picker over.
    }

    yield put(getCoachesSuccess(toCoachOptions(members, presence)));
  } catch (error) {
    yield put(getCoachesFailure(errMessage(error, 'Could not load the coach roster')));
  }
}

export default function* coachSaga() {
  yield takeLatest(
    [getCoachRequestsAction.type, setRequestStatusFilter.type, setRequestPage.type],
    handleGetRequests
  );
  yield takeEvery(decideCoachRequestAction.type, handleDecide);
  yield takeLatest(getCoachesAction.type, handleGetCoaches);
}
